import { Box, Text, VStack, HStack, Icon, Badge, Link } from "@chakra-ui/react";
import { FiExternalLink, FiZap } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { BaseBlockProps } from "./types";

interface CardContent {
  title?: string;
  description?: string;
  url?: string;
}

interface CardMetadata {
  entity_type?: string;
  flow_id?: string;
}

export const CardBlock = ({ block }: BaseBlockProps) => {
  const navigate = useNavigate();

  // Handle both flat and nested content structure
  const content = block.content as CardContent | string | undefined;
  const metadata = block.metadata as CardMetadata | undefined;

  const title =
    typeof content === "object" ? content?.title : block.title || "";
  const description =
    typeof content === "object"
      ? content?.description
      : block.description || "";
  const url = typeof content === "object" ? content?.url : block.url;

  // Check if URL is internal
  const isInternalLink =
    url &&
    (url.startsWith("/") ||
      (!url.startsWith("http://") && !url.startsWith("https://")));

  const isExternalLink =
    url && (url.startsWith("http://") || url.startsWith("https://"));

  // Flow card
  if (metadata?.entity_type === "flow" && metadata?.flow_id) {
    const flowComponent = (
      <Box
        borderRadius="component.md"
        bg="p.50"
        p="md"
        cursor={url ? "pointer" : "default"}
        transition="all 0.2s"
        position="relative"
        _hover={url ? { bg: "p.100", transform: "translateY(-1px)" } : {}}
      >
        <HStack spacing="md" align="start">
          <Box
            bg="white"
            borderRadius="component.md"
            p={1.5}
            boxShadow="sm"
            w={8}
            h={8}
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
          >
            <Icon as={FiZap} boxSize={4} color="p.500" />
          </Box>
          <VStack align="start" spacing="2xs" flex={1}>
            <Text fontSize="sm" fontWeight="600" color="text.body.primary">
              {title}
            </Text>
            {description && (
              <Text fontSize="xs" color="text.body.secondary" lineHeight="short">
                {description}
              </Text>
            )}
            <Badge colorScheme="blue" size="sm" fontSize="xs">
              Flow
            </Badge>
          </VStack>
        </HStack>
      </Box>
    );

    if (url) {
      return (
        <Link
          href={url}
          onClick={(e) => {
            if (isInternalLink) {
              e.preventDefault();
              navigate(url);
            }
          }}
          _hover={{ textDecoration: "none" }}
          display="block"
        >
          {flowComponent}
        </Link>
      );
    }
    return flowComponent;
  }

  // Default card with URL
  if (url) {
    return (
      <Link
        href={url}
        onClick={(e) => {
          if (isInternalLink) {
            e.preventDefault();
            navigate(url);
          }
        }}
        display="block"
        _hover={{ textDecoration: "none" }}
        isExternal={!!isExternalLink}
      >
        <Box
          borderRadius="component.md"
          bg="neutral.50"
          p="md"
          cursor="pointer"
          transition="all 0.2s"
          position="relative"
          _hover={{ bg: "neutral.100", transform: "translateY(-1px)" }}
        >
          <HStack justify="space-between" align="start">
            <VStack align="start" spacing="2xs" flex={1}>
              <Text fontSize="sm" fontWeight="600" color="text.body.primary">
                {title}
              </Text>
              {description && (
                <Text fontSize="xs" color="text.body.secondary" lineHeight="short">
                  {description}
                </Text>
              )}
            </VStack>
            {isExternalLink && (
              <Icon
                as={FiExternalLink}
                boxSize={3}
                color="text.body.secondary"
                flexShrink={0}
              />
            )}
          </HStack>
        </Box>
      </Link>
    );
  }

  // Default card without URL
  return (
    <Box borderRadius="component.md" bg="neutral.50" p="md">
      <Text
        fontSize="sm"
        fontWeight="600"
        color="text.body.primary"
        mb={description ? "2xs" : 0}
      >
        {title}
      </Text>
      {description && (
        <Text fontSize="xs" color="text.body.secondary" lineHeight="short">
          {description}
        </Text>
      )}
    </Box>
  );
};
